import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { setOnboardingStepDone } from '@/lib/storage';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { Button } from '@/components';
import { FiGrid, FiFile, FiHelpCircle, FiSmartphone } from 'react-icons/fi';

const TOTAL_STEPS = 11;

const TOOL_OPTIONS = [
  { id: 'spreadsheet', label: 'Spreadsheet', icon: FiGrid },
  { id: 'paper', label: 'Paper / receipts', icon: FiFile },
  { id: 'nothing', label: 'Nothing organized', icon: FiHelpCircle },
  { id: 'another_app', label: 'Another app', icon: FiSmartphone },
];

function CurrentToolsPage() {
  const navigate = useNavigate();
  const [selectedTool, setSelectedTool] = useState<string | null>(null);

  useEffect(() => {
    track('Current Tools Screen');
  }, []);

  const handleContinue = () => {
    if (selectedTool) {
      track('Current Tools Submitted', { tool: selectedTool });
    }
    setOnboardingStepDone('current_tools');
    navigate({ to: '/app/onboarding/pain-point' });
  };

  const handleSkip = () => {
    track('Current Tools Skipped');
    setOnboardingStepDone('current_tools');
    navigate({ to: '/app/onboarding/pain-point' });
  };

  return (
    <div className='min-h-screen bg-sky-light flex flex-col'>
      <OnboardingProgress currentStep={6} totalSteps={TOTAL_STEPS} />
      <div className='flex-1 flex flex-col justify-between px-6 max-w-lg mx-auto w-full'>
        <div className='flex-1 pt-5'>
          <h1
            className='text-[28px] font-extrabold text-gray-900 text-center'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            How do you currently track expenses?
          </h1>
          <p
            className='mt-2.5 text-base text-gray-500 text-center leading-relaxed mb-6'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            We'll help you do it better.
          </p>

          <div className='space-y-2.5'>
            {TOOL_OPTIONS.map((option) => {
              const isSelected = selectedTool === option.id;
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => setSelectedTool(option.id)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-colors text-left ${
                    isSelected
                      ? 'border-primary bg-primary/[0.04]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-[10px] flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-primary' : 'bg-primary/[0.08]'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        isSelected ? 'text-white' : 'text-primary'
                      }`}
                    />
                  </div>
                  <span
                    className={`flex-1 text-base ${
                      isSelected
                        ? 'text-gray-900 font-semibold'
                        : 'text-gray-700'
                    }`}
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {option.label}
                  </span>
                  <div
                    className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary' : 'border-gray-200'
                    }`}
                  >
                    {isSelected && (
                      <div className='w-3 h-3 rounded-full bg-primary' />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className='w-full pb-6 pt-4 space-y-2.5'>
          <Button fullWidth onClick={handleContinue} disabled={!selectedTool}>
            Continue
          </Button>
          <button
            onClick={handleSkip}
            className='w-full py-3 text-center text-gray-500 font-semibold hover:text-gray-700 transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/current-tools')({
  component: CurrentToolsPage,
});
